// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

// DECISION: The mapper never throws — each transaction is wrapped in try/catch.
// Errors are collected for display in the review UI rather than failing the
// whole import. All monetary amounts are converted to integer minor units
// (cents for fiat, satoshis for crypto) before any arithmetic.

import type { Account } from '../types.js';
import type { ImportedTransaction } from '../lib/import/types.js';
import type { CoinbaseAccount, CoinbaseBuySellDetails, CoinbaseTransaction } from './coinbase-api.js';

export interface MappingContext {
  accounts: Account[];
  coinbaseAccounts: CoinbaseAccount[];
  coinbaseAccountMapping: Record<string, string>; // coinbase_id → ledger account uuid
  defaultCommissionAccount: string | null;
  homeCurrency: string;
}

export interface MappingResult {
  transaction: ImportedTransaction | null;
  error: string | null;
  coinbaseTxId: string;
}

// ─── Amount helpers ───────────────────────────────────────────────────────────

// DECISION: Crypto currencies use 8 decimal places (satoshi-level precision).
// Fiat currencies use standard decimal places (2 for USD/EUR, etc.).
function decimalPlaces(currency: string): number {
  const fiat = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'HKD', 'SGD'];
  if (fiat.includes(currency.toUpperCase())) {
    return currency.toUpperCase() === 'JPY' ? 0 : 2;
  }
  return 8;
}

function toMinorUnits(amountStr: string, currency: string): number {
  const dp = decimalPlaces(currency);
  const val = parseFloat(amountStr);
  return Math.round(val * Math.pow(10, dp));
}

// ─── Account lookup ───────────────────────────────────────────────────────────

const IMBALANCE_ACCOUNT_NAME = 'Imbalance';
const CRYPTO_TRANSFER_ACCOUNT_NAME = 'Expenses:Crypto Transfers';
const CRYPTO_RECEIVED_ACCOUNT_NAME = 'Income:Crypto Received';
const CRYPTO_INTEREST_ACCOUNT_NAME = 'Income:Crypto Interest';

function findAccountByName(accounts: Account[], name: string): string | null {
  const lower = name.toLowerCase();
  const found = accounts.find((a) => a.name.toLowerCase().includes(lower));
  return found?.id ?? null;
}

function resolveLedgerAccount(
  coinbaseAccountId: string,
  context: MappingContext,
): string | null {
  return context.coinbaseAccountMapping[coinbaseAccountId] ?? null;
}

function withTransfer(accountId: string | null): { transferAccountId: string } | Record<never, never> {
  return accountId !== null ? { transferAccountId: accountId } : {};
}

// ─── Core mapping ─────────────────────────────────────────────────────────────

export function mapCoinbaseTransaction(
  tx: CoinbaseTransaction,
  buyDetails: CoinbaseBuySellDetails | null,
  sellDetails: CoinbaseBuySellDetails | null,
  coinbaseAccountId: string,
  context: MappingContext,
): ImportedTransaction | null {
  // Only import completed transactions
  if (tx.status !== 'completed') return null;

  // Skip internal Coinbase movements — not real financial events
  if (tx.type === 'exchange_deposit' || tx.type === 'exchange_withdrawal') return null;

  const fitid = `coinbase-${tx.id}`;
  const date = tx.created_at.slice(0, 10);
  const ledgerAccountId = resolveLedgerAccount(coinbaseAccountId, context);
  const nativeCurrency = tx.native_amount.currency;
  const nativeMinor = Math.abs(toMinorUnits(tx.native_amount.amount, nativeCurrency));
  const cryptoCurrency = tx.amount.currency;
  const cryptoMinor = Math.abs(toMinorUnits(tx.amount.amount, cryptoCurrency));

  switch (tx.type) {
    case 'buy': {
      // User bought crypto with fiat
      // Split 1: Coinbase fiat account  -total
      // Split 2: Crypto asset account   +subtotal (fiat value) with quantity
      // Split 3: Commission expense     +fee

      if (!buyDetails) {
        return buildSimpleImport(tx, coinbaseAccountId, ledgerAccountId, context, fitid);
      }

      const totalMinor = toMinorUnits(buyDetails.total.amount, nativeCurrency);
      const subtotalMinor = toMinorUnits(buyDetails.subtotal.amount, nativeCurrency);
      const feeMinor = totalMinor - subtotalMinor;

      const fiatAccount = ledgerAccountId ?? findAccountByName(context.accounts, 'coinbase usd') ?? findAccountByName(context.accounts, 'coinbase') ?? null;
      const cryptoAccount = findAccountByName(context.accounts, cryptoCurrency.toLowerCase() + ' coinbase') ?? findAccountByName(context.accounts, cryptoCurrency) ?? null;
      const commissionAccount = context.defaultCommissionAccount ?? findAccountByName(context.accounts, 'commission') ?? findAccountByName(context.accounts, 'fee') ?? null;

      return {
        date,
        description: `Coinbase: Buy ${cryptoCurrency}`,
        amount: nativeMinor,
        currency: nativeCurrency,
        accountId: cryptoAccount ?? coinbaseAccountId,
        fitid,
        notes: feeMinor > 0 ? `Fee: ${buyDetails.fees.map((f) => f.amount.amount + ' ' + f.amount.currency).join(', ')}` : '',
        rawData: {
          coinbaseType: tx.type,
          fiatAccount: fiatAccount ?? '',
          cryptoAccount: cryptoAccount ?? '',
          commissionAccount: commissionAccount ?? '',
          totalMinor: String(totalMinor),
          subtotalMinor: String(subtotalMinor),
          feeMinor: String(feeMinor),
          cryptoMinor: String(cryptoMinor),
          cryptoCurrency,
          nativeCurrency,
        },
      };
    }

    case 'sell': {
      // User sold crypto for fiat
      if (!sellDetails) {
        return buildSimpleImport(tx, coinbaseAccountId, ledgerAccountId, context, fitid);
      }

      const totalMinor = toMinorUnits(sellDetails.total.amount, nativeCurrency);
      const subtotalMinor = toMinorUnits(sellDetails.subtotal.amount, nativeCurrency);
      const feeMinor = subtotalMinor - totalMinor;

      return {
        date,
        description: `Coinbase: Sell ${cryptoCurrency}`,
        amount: -nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        fitid,
        notes: feeMinor > 0 ? `Fee: ${sellDetails.fees.map((f) => f.amount.amount + ' ' + f.amount.currency).join(', ')}` : '',
        rawData: {
          coinbaseType: tx.type,
          totalMinor: String(totalMinor),
          subtotalMinor: String(subtotalMinor),
          feeMinor: String(feeMinor),
          cryptoMinor: String(cryptoMinor),
          cryptoCurrency,
          nativeCurrency,
        },
      };
    }

    case 'send': {
      // Treated as disposal — taxable event warning added
      return {
        date,
        description: `Coinbase: Send ${cryptoCurrency}${tx.description ? ` (${tx.description})` : ''}`,
        amount: -nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        ...withTransfer(findAccountByName(context.accounts, CRYPTO_TRANSFER_ACCOUNT_NAME)),
        fitid,
        notes: 'WARNING: Sending crypto may be a taxable event. Consult a tax professional.',
        rawData: { coinbaseType: tx.type },
      };
    }

    case 'receive': {
      return {
        date,
        description: `Coinbase: Receive ${cryptoCurrency}${tx.description ? ` (${tx.description})` : ''}`,
        amount: nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        ...withTransfer(findAccountByName(context.accounts, CRYPTO_RECEIVED_ACCOUNT_NAME)),
        fitid,
        rawData: { coinbaseType: tx.type },
      };
    }

    case 'fiat_deposit': {
      return {
        date,
        description: 'Coinbase: USD deposit',
        amount: nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        ...withTransfer(findAccountByName(context.accounts, IMBALANCE_ACCOUNT_NAME)),
        fitid,
        notes: 'Source account unknown — please assign in review.',
        rawData: { coinbaseType: tx.type },
      };
    }

    case 'fiat_withdrawal': {
      return {
        date,
        description: 'Coinbase: USD withdrawal',
        amount: -nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        ...withTransfer(findAccountByName(context.accounts, IMBALANCE_ACCOUNT_NAME)),
        fitid,
        notes: 'Destination account unknown — please assign in review.',
        rawData: { coinbaseType: tx.type },
      };
    }

    case 'interest':
    case 'staking_reward':
    case 'inflation_reward': {
      const typeLabel = tx.type === 'interest' ? 'interest'
        : tx.type === 'staking_reward' ? 'staking reward'
        : 'inflation reward';
      return {
        date,
        description: `Coinbase: ${cryptoCurrency} ${typeLabel}`,
        amount: nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        ...withTransfer(findAccountByName(context.accounts, CRYPTO_INTEREST_ACCOUNT_NAME)),
        fitid,
        rawData: { coinbaseType: tx.type },
      };
    }

    case 'vault_withdrawal': {
      return {
        date,
        description: `Coinbase: Vault withdrawal ${cryptoCurrency}`,
        amount: nativeMinor,
        currency: nativeCurrency,
        accountId: ledgerAccountId ?? coinbaseAccountId,
        fitid,
        rawData: { coinbaseType: tx.type },
      };
    }

    default:
      return null;
  }
}

function typeDescription(tx: CoinbaseTransaction): string {
  const currency = tx.amount.currency;
  switch (tx.type) {
    case 'buy': return `Buy ${currency}`;
    case 'sell': return `Sell ${currency}`;
    case 'send': return `Send ${currency}`;
    case 'receive': return `Receive ${currency}`;
    case 'fiat_deposit': return 'USD deposit';
    case 'fiat_withdrawal': return 'USD withdrawal';
    case 'interest': return `${currency} interest`;
    case 'staking_reward': return `${currency} staking reward`;
    case 'inflation_reward': return `${currency} inflation reward`;
    default: return tx.details.title || tx.type;
  }
}

function buildSimpleImport(
  tx: CoinbaseTransaction,
  coinbaseAccountId: string,
  ledgerAccountId: string | null,
  _context: MappingContext,
  fitid: string,
): ImportedTransaction {
  const nativeCurrency = tx.native_amount.currency;
  const nativeMinor = toMinorUnits(tx.native_amount.amount, nativeCurrency);
  return {
    date: tx.created_at.slice(0, 10),
    description: `Coinbase: ${typeDescription(tx)}`,
    amount: nativeMinor,
    currency: nativeCurrency,
    accountId: ledgerAccountId ?? coinbaseAccountId,
    fitid,
    notes: tx.description ?? '',
    rawData: { coinbaseType: tx.type },
  };
}

// ─── Batch mapping ────────────────────────────────────────────────────────────

export function mapCoinbaseTransactions(
  transactions: CoinbaseTransaction[],
  buyDetailsMap: Map<string, CoinbaseBuySellDetails>,
  sellDetailsMap: Map<string, CoinbaseBuySellDetails>,
  coinbaseAccountId: string,
  context: MappingContext,
): MappingResult[] {
  return transactions.map((tx) => {
    try {
      const buyDetails = tx.buy?.id ? (buyDetailsMap.get(tx.buy.id) ?? null) : null;
      const sellDetails = tx.sell?.id ? (sellDetailsMap.get(tx.sell.id) ?? null) : null;
      const transaction = mapCoinbaseTransaction(
        tx, buyDetails, sellDetails, coinbaseAccountId, context,
      );
      return { transaction, error: null, coinbaseTxId: tx.id };
    } catch (err) {
      return {
        transaction: null,
        error: err instanceof Error ? err.message : 'Unknown mapping error',
        coinbaseTxId: tx.id,
      };
    }
  });
}
